/**
 * The gift certificate in someone's hands, built in the browser like the memory book's scene
 * (book-scene.ts): a fixed photo of a blank certificate, and the page's own words laid on the
 * sheet and warped onto it. One photo for every market; the words follow the page's language.
 */
import { quadTransform } from "./book-scene";

export const CERTIFICATE_PHOTO =
  "/wp-content/assets/images/memorial-gift/photo.jpg";

export const CERTIFICATE_SIZE = { height: 1086, width: 1448 } as const;

/** The sheet's corners in the photo (TL, TR, BR, BL), fitted to its edges. */
const QUAD: readonly [number, number][] = [
  [642.8, 150.7],
  [1082.9, 289.1],
  [847.7, 929.7],
  [340.3, 765.2],
];

/** The sheet laid flat: A4, before the warp. */
export const CERTIFICATE_SHEET = { height: 1188, width: 840 } as const;

/** The photo's middle: the card crops it as it did the static image. */
export const CERTIFICATE_FOCUS = { x: 724, y: 543 } as const;

export function sheetTransform(): string {
  return quadTransform(CERTIFICATE_SHEET, QUAD);
}

/** The heading's room on the sheet, its size, and Calluna Sans' average letter width (em). */
const TEXT_WIDTH = 610;
const HEADING_SIZE = 64;
const MIN_SIZE = 36;
const LETTER = 0.5;
const SPACES = /\s+/;

/**
 * The name's font size on the sheet: the certificate's heading size, smaller for long names so
 * every word fits a line and the whole name keeps to two lines above the drawing.
 */
export function nameSize(name: string): number {
  const words = name.trim().split(SPACES);
  const longest = Math.max(1, ...words.map((word) => word.length));
  const byWord = TEXT_WIDTH / (longest * LETTER);
  // 1.8, not 2: lines break between words, never quite full.
  const byLines = (1.8 * TEXT_WIDTH) / (Math.max(1, name.length) * LETTER);
  return Math.floor(
    Math.max(MIN_SIZE, Math.min(HEADING_SIZE, byWord, byLines))
  );
}
